import { Scene, Mesh, BaseTexture, CubeTexture, Texture, EquiRectangularCubeTexture, MeshBuilder, StandardMaterial } from '@babylonjs/core';
import { logger } from '../core/Logger';

interface EnvironmentConfig {
  type: 'skybox' | 'reflection';
  use_mesh_material: boolean;
  envIntensity: number;
  skyboxBrightness: number;
  rotation: number;
  blur: number;
}

/**
 * Manages environment/skybox setup from Blender entities
 */
export class EnvironmentManager {
  private scene: Scene;
  private currentSkybox: Mesh | null = null;
  private currentConfig: EnvironmentConfig | null = null;
  private currentTexture: BaseTexture | null = null;
  private currentHdrUrl: string | null = null;
  private currentEnvIntensity: number = 1.0;
  private currentSkyboxBrightness: number = 0.5;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * Process an environment entity mesh
   */
  processEnvironmentEntity(mesh: Mesh, extras: any): void {
    // Read behavior parameters (hm_behavior_*) from schema
    const intensity = this.getBehaviorParam(extras, 'intensity', 1.0);
    // sky_brightness defaults to intensity * 0.5 if not specified
    const skyBrightness = this.getBehaviorParam(extras, 'sky_brightness', intensity * 0.5);
    const config: EnvironmentConfig = {
      type: this.getBehaviorParam(extras, 'type', 'skybox'),
      use_mesh_material: this.getBehaviorParam(extras, 'use_mesh_material', true),
      envIntensity: intensity,
      skyboxBrightness: skyBrightness,
      rotation: this.getBehaviorParam(extras, 'rotation_y', 0.0),
      blur: this.getBehaviorParam(extras, 'blur_level', 0.0),
    };

    // Check for HDR URL - if provided, load HDR instead of mesh material
    const hdrUrl = this.getBehaviorParam(extras, 'hdr_url', '');
    if (hdrUrl) {
      logger.environment.log(`Loading HDR environment from URL: ${hdrUrl}`);
      this.loadHdrFromUrl(hdrUrl, config.envIntensity, config.skyboxBrightness);
      mesh.isVisible = false;
      return;
    }

    logger.environment.log(`Processing environment entity: ${mesh.name} (type: ${config.type})`);
    logger.environment.debug(`Material: ${mesh.material?.name}, Use mesh material: ${config.use_mesh_material}`);

    if (!config.use_mesh_material) {
      logger.environment.warn(`Environment entity ${mesh.name} has use_mesh_material=false, but no alternative texture source implemented yet`);
      mesh.isVisible = false;
      return;
    }

    // Get the texture from the mesh's material
    const texture = this.extractTextureFromMaterial(mesh);
    
    // If no texture, we can still use the material with just a color
    if (!texture) {
      logger.environment.debug(`No texture found, will use material color for skybox`);
      this.createColoredSkybox(mesh, config);
      return;
    }

    logger.environment.debug(`Found texture: ${texture.name}`);

    // Store current config and texture
    this.currentConfig = config;
    this.currentTexture = texture;

    // Apply environment based on type
    if (config.type === 'skybox') {
      this.createSkybox(mesh, texture, config);
    } else if (config.type === 'reflection') {
      this.applyReflectionOnly(texture, config);
      mesh.isVisible = false;
    }
  }

  /**
   * Extract texture from mesh material
   */
  private extractTextureFromMaterial(mesh: Mesh): BaseTexture | null {
    logger.environment.debug(`Extracting texture from material...`);
    
    if (!mesh.material) {
      logger.environment.warn(`Mesh has no material!`);
      return null;
    }

    const material = mesh.material as any;
    logger.environment.debug(`Material class: ${material.getClassName()}`);
    
    // Log all properties that might contain textures
    const textureProps: string[] = [];
    for (const key in material) {
      if (key.toLowerCase().includes('texture') && material[key]) {
        textureProps.push(`${key}: ${material[key]?.name || material[key]?.constructor?.name || 'unknown'}`);
      }
    }
    if (textureProps.length > 0) {
      logger.environment.debug(`Material texture properties:`, textureProps);
    } else {
      logger.environment.debug(`Material has NO texture properties at all`);
    }

    // Try common texture slots
    const candidates = [
      'albedoTexture',
      'baseTexture', 
      'diffuseTexture',
      'emissiveTexture',
    ];

    for (const slot of candidates) {
      if (material[slot] && material[slot] instanceof BaseTexture) {
        logger.environment.debug(`Found texture in slot: ${slot} - ${material[slot].name}`);
        return material[slot];
      } else if (material[slot]) {
        logger.environment.debug(`Slot ${slot} has value but not a BaseTexture:`, material[slot]);
      }
    }

    logger.environment.warn(`No texture found in any candidate slots`);
    logger.environment.info(`In Blender, make sure your material has a texture connected to Base Color/Albedo`);
    return null;
  }

  /**
   * Create a skybox from the environment entity
   */
  private createSkybox(originalMesh: Mesh, texture: BaseTexture, config: EnvironmentConfig): void {
    logger.environment.debug(`Creating skybox from mesh: ${originalMesh.name}`);
    
    // Remove previous skybox if it exists
    if (this.currentSkybox) {
      this.currentSkybox.dispose();
      this.currentSkybox = null;
    }

    // Hide the original mesh (we'll create our own skybox)
    originalMesh.isVisible = false;
    originalMesh.checkCollisions = false;

    // Clone the mesh for the skybox (to preserve the original geometry)
    const skybox = originalMesh.clone(`skybox_${originalMesh.name}`, null, true);
    if (!skybox) {
      logger.environment.error('Failed to clone skybox mesh');
      return;
    }

    // Configure skybox properties
    skybox.isVisible = true;
    skybox.infiniteDistance = true; // Always at horizon
    skybox.renderingGroupId = 0; // Render first
    skybox.isPickable = false;
    skybox.checkCollisions = false;

    // Scale the skybox to be very large
    const scale = 1000;
    skybox.scaling.setAll(scale);

    // Apply rotation
    if (config.rotation !== 0) {
      skybox.rotation.y = (config.rotation * Math.PI) / 180;
    }

    logger.environment.debug(`Skybox cloned, material: ${skybox.material?.name}`);

    // Flip the mesh inside-out so we can see it from the inside
    skybox.scaling.x *= -1; // Invert on one axis to flip normals

    // Configure material for skybox rendering
    if (skybox.material) {
      const mat = skybox.material as any;
      
      logger.environment.debug(`Configuring material - type: ${mat.getClassName()}`);
      
      // CRITICAL: Disable backface culling so we see the inside
      mat.backFaceCulling = false;
      
      // Disable lighting for skybox
      mat.disableLighting = true;
      
      // Make it unlit by using emissive - use skyboxBrightness for visible sky
      if ('emissiveColor' in mat) {
        mat.emissiveColor.set(config.skyboxBrightness, config.skyboxBrightness, config.skyboxBrightness);
      }
      
      // Move texture to emissive slot so it's self-lit
      if ('emissiveTexture' in mat) {
        mat.emissiveTexture = texture;
        logger.environment.debug(`Set emissive texture: ${texture.name}`);
      }
      
      // Also keep albedo texture
      if ('albedoTexture' in mat && !mat.albedoTexture) {
        mat.albedoTexture = texture;
        logger.environment.debug(`Set albedo texture: ${texture.name}`);
      }
    } else {
      logger.environment.warn('Skybox has no material!');
    }

    // Set as environment texture for reflections - use envIntensity for IBL
    this.scene.environmentTexture = texture as any;
    this.scene.environmentIntensity = config.envIntensity;

    this.currentSkybox = skybox;
    logger.environment.log(`Skybox created: ${skybox.name} (envIntensity: ${config.envIntensity}, skyboxBrightness: ${config.skyboxBrightness})`);
  }

  /**
   * Create a colored skybox from the environment entity (no texture, just material color)
   */
  private createColoredSkybox(originalMesh: Mesh, config: EnvironmentConfig): void {
    logger.environment.debug(`Creating colored skybox from mesh: ${originalMesh.name}`);
    
    // Remove previous skybox if it exists
    if (this.currentSkybox) {
      this.currentSkybox.dispose();
      this.currentSkybox = null;
    }

    // Hide the original mesh
    originalMesh.isVisible = false;
    originalMesh.checkCollisions = false;

    // Clone the mesh for the skybox
    const skybox = originalMesh.clone(`skybox_${originalMesh.name}`, null, true);
    if (!skybox) {
      logger.environment.error('Failed to clone skybox mesh');
      return;
    }

    // Configure skybox properties
    skybox.isVisible = true;
    skybox.infiniteDistance = true;
    skybox.renderingGroupId = 0;
    skybox.isPickable = false;
    skybox.checkCollisions = false;

    // Scale and flip
    const scale = 1000;
    skybox.scaling.set(-scale, scale, scale); // Negative X to flip inside-out

    // Apply rotation
    if (config.rotation !== 0) {
      skybox.rotation.y = (config.rotation * Math.PI) / 180;
    }

    logger.environment.debug(`Skybox cloned, using material color`);

    // Configure material for colored skybox
    if (skybox.material) {
      const mat = skybox.material as any;
      
      logger.environment.debug(`Configuring material - type: ${mat.getClassName()}`);
      
      // CRITICAL: Disable backface culling
      mat.backFaceCulling = false;
      
      // Disable lighting
      mat.disableLighting = true;
      
      // Use emissive to make it visible and self-lit
      if ('emissiveColor' in mat && 'albedoColor' in mat) {
        // Copy albedo color to emissive so it's visible without lighting
        const albedoColor = mat.albedoColor || mat.diffuseColor;
        if (albedoColor) {
          mat.emissiveColor.copyFrom(albedoColor);
          mat.emissiveColor.scaleInPlace(config.skyboxBrightness);
          logger.environment.debug(`Set emissive color from albedo: (${albedoColor.r.toFixed(2)}, ${albedoColor.g.toFixed(2)}, ${albedoColor.b.toFixed(2)})`);
        }
      }
    }

    // Store current config
    this.currentConfig = config;
    this.currentTexture = null;

    this.currentSkybox = skybox;
    logger.environment.log(`Colored skybox created: ${skybox.name} (skyboxBrightness: ${config.skyboxBrightness})`);
  }

  /**
   * Apply texture as reflection environment only (no visible skybox)
   */
  private applyReflectionOnly(texture: BaseTexture, config: EnvironmentConfig): void {
    this.scene.environmentTexture = texture as any;
    this.scene.environmentIntensity = config.envIntensity;
    logger.level.log(`✅ Reflection environment applied (envIntensity: ${config.envIntensity})`);
  }

  /**
   * Get behavior parameter (hm_behavior_*)
   */
  private getBehaviorParam<T>(extras: any, paramName: string, defaultValue: T): T {
    if (!extras) return defaultValue;
    const key = `hm_behavior_${paramName}`;
    return extras[key] ?? defaultValue;
  }

  /**
   * Clear current environment
   */
  clear(): void {
    if (this.currentSkybox) {
      this.currentSkybox.dispose();
      this.currentSkybox = null;
    }
    this.scene.environmentTexture = null;
    this.currentConfig = null;
    this.currentTexture = null;
    this.currentHdrUrl = null;
  }

  /**
   * Load HDR environment texture from URL
   * Supports:
   * - .env files (BabylonJS prefiltered format - RECOMMENDED)
   * - .hdr files (equirectangular HDR images)
   * - .jpg/.png (equirectangular LDR images - limited quality)
   */
  loadHdrFromUrl(url: string, envIntensity: number = 1.0, skyboxBrightness: number = 0.5): void {
    if (!url) {
      logger.environment.warn('No HDR URL provided');
      return;
    }

    // Store values
    this.currentEnvIntensity = envIntensity;
    this.currentSkyboxBrightness = skyboxBrightness;

    logger.environment.log(`Loading environment texture from: ${url}`);
    
    // Clear existing skybox mesh if any
    if (this.currentSkybox) {
      this.currentSkybox.dispose();
      this.currentSkybox = null;
    }

    // Determine texture type based on extension
    const lowerUrl = url.toLowerCase();

    try {
      if (lowerUrl.endsWith('.env')) {
        // .env files are pre-filtered cube textures (BabylonJS format) - BEST
        const texture = CubeTexture.CreateFromPrefilteredData(url, this.scene);
        texture.onLoadObservable.addOnce(() => {
          logger.environment.log(`✅ Environment texture loaded: ${url}`);
        });
        this.applyEnvironmentTexture(texture, url, envIntensity, skyboxBrightness);
        logger.environment.debug('Created CubeTexture from .env file');
        
      } else if (lowerUrl.endsWith('.hdr') || lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.png')) {
        // Equirectangular images (HDR or LDR)
        // Use EquiRectangularCubeTexture which converts equirectangular to cubemap
        const size = 256; // Cubemap face size
        const texture = new EquiRectangularCubeTexture(
          url,
          this.scene,
          size,
          false, // noMipmap
          true,  // gammaSpace (false for HDR, true for LDR)
          () => {
            logger.environment.log(`✅ Environment texture loaded: ${url}`);
          },
          (message, exception) => {
            logger.environment.error(`Failed to load environment texture: ${message}`, exception);
          }
        );
        this.applyEnvironmentTexture(texture, url, envIntensity, skyboxBrightness);
        logger.environment.debug(`Created EquiRectangularCubeTexture from ${lowerUrl.endsWith('.hdr') ? 'HDR' : 'LDR'} image`);
        
      } else if (lowerUrl.endsWith('.dds')) {
        // DDS cube maps
        const texture = CubeTexture.CreateFromPrefilteredData(url, this.scene);
        this.applyEnvironmentTexture(texture, url, envIntensity, skyboxBrightness);
        logger.environment.debug('Created CubeTexture from .dds file');
        
      } else {
        logger.environment.error(`Unsupported environment texture format: ${url}`);
        logger.environment.info(`Supported formats: .env (recommended), .hdr, .jpg, .png, .dds`);
        return;
      }
    } catch (error) {
      logger.environment.error(`Failed to load environment texture: ${error}`);
    }
  }

  /**
   * Apply a texture as the environment texture and create a visible skybox
   */
  private applyEnvironmentTexture(texture: BaseTexture, url: string, envIntensity: number, skyboxBrightness: number): void {
    // Apply as environment texture for reflections/IBL
    this.scene.environmentTexture = texture;
    this.scene.environmentIntensity = envIntensity;
    
    // Create visible skybox from the environment texture
    this.createSkyboxFromCubemap(texture as CubeTexture, skyboxBrightness);
    
    // Store references
    this.currentTexture = texture;
    this.currentHdrUrl = url;
    this.currentEnvIntensity = envIntensity;
    this.currentSkyboxBrightness = skyboxBrightness;
    this.currentConfig = {
      type: 'skybox',
      use_mesh_material: false,
      envIntensity,
      skyboxBrightness,
      rotation: 0,
      blur: 0
    };
  }

  /**
   * Create a visible skybox mesh from a cubemap texture
   */
  private createSkyboxFromCubemap(texture: CubeTexture | EquiRectangularCubeTexture, skyboxBrightness: number): void {
    // Remove previous skybox if exists
    if (this.currentSkybox) {
      this.currentSkybox.dispose();
      this.currentSkybox = null;
    }

    // Create a large box for the skybox
    const skybox = MeshBuilder.CreateBox('hdrSkybox', { size: 1000 }, this.scene);
    skybox.infiniteDistance = true;
    skybox.renderingGroupId = 0;
    skybox.isPickable = false;
    
    // Create material for the skybox
    const skyboxMaterial = new StandardMaterial('hdrSkyboxMaterial', this.scene);
    skyboxMaterial.backFaceCulling = false;
    skyboxMaterial.disableLighting = true;
    
    // Use the cubemap as reflection texture (this displays the environment on the inside of the box)
    skyboxMaterial.reflectionTexture = texture.clone();
    skyboxMaterial.reflectionTexture!.coordinatesMode = Texture.SKYBOX_MODE;
    
    // Control brightness via texture level - this actually multiplies the texture
    skyboxMaterial.reflectionTexture!.level = skyboxBrightness;
    
    // Also set diffuseColor to black so only reflection shows
    skyboxMaterial.diffuseColor.set(0, 0, 0);
    skyboxMaterial.specularColor.set(0, 0, 0);
    
    skybox.material = skyboxMaterial;
    this.currentSkybox = skybox;
    
    logger.environment.log(`✅ Skybox created from HDR environment (brightness: ${skyboxBrightness})`);
  }

  /**
   * Set environment IBL intensity (reflections)
   */
  setEnvIntensity(value: number): void {
    this.scene.environmentIntensity = value;
    this.currentEnvIntensity = value;
    if (this.currentConfig) {
      this.currentConfig.envIntensity = value;
    }
    logger.environment.debug(`Environment IBL intensity set to ${value}`);
  }

  /**
   * Set skybox brightness (visible sky)
   */
  setSkyboxBrightness(value: number): void {
    this.currentSkyboxBrightness = value;
    if (this.currentConfig) {
      this.currentConfig.skyboxBrightness = value;
    }
    
    // Update the skybox material's reflection texture level
    if (this.currentSkybox?.material) {
      const mat = this.currentSkybox.material as StandardMaterial;
      if (mat.reflectionTexture) {
        mat.reflectionTexture.level = value;
      }
    }
    
    logger.environment.debug(`Skybox brightness set to ${value}`);
  }

  /**
   * Get info about current environment for debug UI
   */
  getInfo(): any {
    if (!this.currentSkybox && !this.currentTexture) {
      return { loaded: false };
    }

    return {
      loaded: true,
      name: this.currentSkybox?.name,
      type: this.currentConfig?.type || 'skybox',
      envIntensity: this.currentEnvIntensity,
      skyboxBrightness: this.currentSkyboxBrightness,
      rotation: this.currentConfig?.rotation || 0,
      textureName: this.currentTexture?.name,
      hdrUrl: this.currentHdrUrl,
    };
  }
}
